import { useMemo, useState } from "react";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import SkillEditorModal, {
  type SkillFormData,
  type SkillTag,
} from "~/components/assistant/SkillEditorModal";
import type { Skill } from "~/components/assistant/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useErrorToast from "~/contexts/error-toast";
import { useDatasetSkills } from "~/pages/datasets/components/assistant/hooks/useDatasetSkills";
import rumClient from "~/utils/monitoring";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

const DatasetSkillsTab = () => {
  const { data: dataset } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);

  // Delete confirmation state
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);

  const datasetId = dataset?.id;
  const {
    skills,
    isLoading: loading,
    error,
    toggle,
    createSkill,
    updateSkill,
    deleteSkill,
  } = useDatasetSkills(api, datasetId);
  const fetchError = !!error;

  const handleToggle = async (skillId: string, enabled: boolean) => {
    try {
      await toggle(skillId, enabled);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to update skill");
    }
  };

  const handleAddSkill = () => {
    setEditingSkill(null);
    setEditorOpen(true);
  };

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await api.getSkill(skillId);
      setEditingSkill(skill);
      setEditorOpen(true);
    } catch (e) {
      console.error(e);
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (data: SkillFormData) => {
    setEditorLoading(true);
    try {
      if (editingSkill) {
        await updateSkill(editingSkill.id, {
          description: data.description,
          content: data.content,
          tags: data.tags,
        });
      } else {
        await createSkill(data);
      }
      setEditorOpen(false);
      setEditingSkill(null);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setEditorLoading(false);
    }
  };

  const editorInitialData = useMemo(
    () =>
      editingSkill
        ? {
            name: editingSkill.name,
            description: editingSkill.description ?? "",
            content: editingSkill.content,
            tags: (editingSkill.tags ?? []) as SkillTag[],
          }
        : undefined,
    [editingSkill]
  );

  const handleDeleteSkill = async () => {
    if (!deletingSkillId) return;
    setIsDeleting(deletingSkillId);
    try {
      await deleteSkill(deletingSkillId);
      setDeletingSkillId(null);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to delete skill");
    } finally {
      setIsDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <p className="text-sm text-destructive">
        Failed to load dataset skills. Please try refreshing the page.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Skills provide domain-specific instructions and workflows for AI Analysis conversations on
          this dataset.
        </p>

        {skills.length > 0 && (
          <div className="flex flex-col gap-1">
            {skills.map((skill) => (
              <div key={skill.id} className="flex items-center justify-between py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{skill.name}</p>
                  {skill.description && (
                    <p className="truncate text-[13px] text-muted-foreground">
                      {skill.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {isOwner && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => handleEditSkill(skill.id)}
                        aria-label={`Edit ${skill.name}`}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => setDeletingSkillId(skill.id)}
                        aria-label={`Delete ${skill.name}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </>
                  )}
                  <Switch
                    checked={skill.enabled}
                    disabled={!isOwner}
                    onCheckedChange={(checked) => handleToggle(skill.id, checked)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {isOwner && (
          <Button
            variant="secondary"
            size="sm"
            className="self-start bg-[var(--gray-200)] hover:bg-[var(--gray-300)]"
            onClick={handleAddSkill}
            aria-label="Add skill"
          >
            <Plus className="size-4" />
            Add Skill
          </Button>
        )}
      </div>

      <AlertDialog open={!!deletingSkillId} onOpenChange={() => setDeletingSkillId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Skill?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;
              {skills.find((s) => s.id === deletingSkillId)?.name}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSkill}
              disabled={!!isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SkillEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingSkill(null);
        }}
        onSubmit={handleEditorSubmit}
        loading={editorLoading}
        isEditing={!!editingSkill}
        initialData={editorInitialData}
      />
    </>
  );
};

export default DatasetSkillsTab;
